import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Layout,
  theme,
  Table,
  Tag,
  Select,
  Tabs,
  Typography,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Statistic,
  Row,
  Col,
  Card,
  Alert,
} from "antd";
import {
  ToolOutlined,
  ArrowLeftOutlined,
  StopOutlined,
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import TopBar from "../../components/TopBar";
import AdminTopBar from "../../components/TopBar/AdminTopbar";
import SideBar from "../../components/SideBar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import { AuthContext } from "../../context/AuthContext";
import {
  approveLapseRunApi,
  excludeLapseRunMembersApi,
  getLapseRunByIdApi,
  includeLapseRunMembersApi,
  LapseRun,
  LapseRunMember,
  LapseRunMemberStatus,
  LapseRunStatus,
  rejectLapseRunApi,
} from "../../services/Admin/LapseRunApis";

const { Content } = Layout;

const MEMBER_STATUS_TABS: { key: LapseRunMemberStatus; label: string }[] = [
  { key: "INCLUDED", label: "Included" },
  { key: "EXCLUDED", label: "Excluded" },
  { key: "LAPSED", label: "Lapsed" },
  { key: "SKIPPED", label: "Skipped" },
  { key: "FAILED", label: "Failed" },
];

const RUN_STATUS_COLOR: Record<LapseRunStatus, string> = {
  PENDING: "orange",
  APPROVED: "blue",
  COMPLETED: "green",
  REJECTED: "default",
  FAILED: "red",
};

const MEMBER_STATUS_COLOR: Record<LapseRunMemberStatus, string> = {
  INCLUDED: "blue",
  EXCLUDED: "default",
  LAPSED: "green",
  SKIPPED: "orange",
  FAILED: "red",
};

const formatCurrency = (value?: string | number | null) => {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(num);
};

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";

const isNotFound = (res: any) =>
  res?.status === 404 ||
  String(res?.message || "")
    .toLowerCase()
    .includes("not found");

const MaintenanceRunPage: React.FC<{ adminLayout?: boolean }> = ({
  adminLayout = false,
}) => {
  const { runId } = useParams();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const [isActive, setActive] = useState(true);
  const [run, setRun] = useState<LapseRun | null>(null);
  const [memberStatus, setMemberStatus] = useState<LapseRunMemberStatus | undefined>(
    "INCLUDED"
  );
  const [owningGroupId, setOwningGroupId] = useState<number | undefined>();
  const [owningGroupOptions, setOwningGroupOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [excludeForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const pollRef = useRef<number | null>(null);

  const role = currentUser?.Role?.Name;
  const isSuperAdmin = role === "Super Admin";
  const listPath = adminLayout ? "/admin/maintenance" : "/maintenance";
  const isPending = run?.status === "PENDING";
  const isProcessing = run?.status === "APPROVED";
  const isIncludedTab = memberStatus === "INCLUDED";
  const isExcludedTab = memberStatus === "EXCLUDED";
  const canSelectMembers =
    isPending && (isIncludedTab || isExcludedTab);

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyRun = useCallback(
    (next: LapseRun, captureGroups: boolean) => {
      setRun(next);
      if (captureGroups) {
        const groups = new Map<number, string>();
        if (next.owningGroupId && next.OwningGroup?.GroupName) {
          groups.set(next.owningGroupId, next.OwningGroup.GroupName);
        }
        (next.members || []).forEach((member) => {
          if (member.owningGroupId && member.OwningGroup?.GroupName) {
            groups.set(member.owningGroupId, member.OwningGroup.GroupName);
          }
        });
        if (groups.size > 0) {
          setOwningGroupOptions(
            Array.from(groups.entries()).map(([value, label]) => ({
              value,
              label,
            }))
          );
        }
      }
    },
    []
  );

  const loadRun = useCallback(
    (opts?: { silent?: boolean; captureGroups?: boolean }) => {
      if (!runId) return Promise.resolve();
      if (!opts?.silent) setActive(true);

      return getLapseRunByIdApi(runId, {
        ...(memberStatus ? { status: memberStatus } : {}),
        ...(owningGroupId != null ? { owningGroupId } : {}),
      })
        .then((res) => {
          if (isNotFound(res)) {
            message.error(res?.message || "Lapse run not found");
            navigate(listPath);
            return;
          }
          if (!res || res.success === false) {
            message.error(res?.message || "Unable to load lapse run.");
            if (!opts?.silent) setRun(null);
            return;
          }
          applyRun(res.data, opts?.captureGroups !== false && owningGroupId == null);
        })
        .catch((err) => {
          console.log(err);
          message.error("Unable to load lapse run.");
        })
        .finally(() => {
          if (!opts?.silent) setActive(false);
        });
    },
    [runId, memberStatus, owningGroupId, applyRun, navigate, listPath]
  );

  useEffect(() => {
    if (role && !isSuperAdmin) {
      navigate(adminLayout ? "/admin/dashboard" : "/dashboard");
    }
  }, [role, isSuperAdmin, navigate, adminLayout]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadRun();
  }, [isSuperAdmin, loadRun]);

  useEffect(() => {
    setSelectedMemberIds([]);
  }, [memberStatus, owningGroupId]);

  useEffect(() => {
    if (run?.status === "APPROVED" && pollRef.current == null) {
      pollRef.current = window.setInterval(() => {
        loadRun({ silent: true, captureGroups: false });
      }, 3000);
    }
    if (run?.status === "COMPLETED" || run?.status === "FAILED") {
      stopPolling();
    }
    return () => stopPolling();
  }, [run?.status, loadRun, stopPolling]);

  const includedCount = useMemo(() => {
    if (typeof run?.includedCount === "number") {
      return run.includedCount;
    }
    const fromMembers = (run?.members || []).filter(
      (member) => member.status === "INCLUDED"
    ).length;
    if (memberStatus === "INCLUDED" && owningGroupId == null && fromMembers > 0) {
      return fromMembers;
    }
    return Math.max((run?.proposedCount ?? 0) - (run?.excludedCount ?? 0), 0);
  }, [run, memberStatus, owningGroupId]);

  const handleExclude = async () => {
    if (!runId || selectedMemberIds.length === 0) {
      message.error("Select at least one member to exclude.");
      return;
    }
    const values = await excludeForm.validateFields();
    setActionLoading(true);
    try {
      const res = await excludeLapseRunMembersApi(runId, {
        memberIds: selectedMemberIds,
        ...(values.excludeReason ? { excludeReason: values.excludeReason } : {}),
      });
      if (!res || res.success === false) {
        message.error(res?.message || "Unable to exclude members.");
        if (res?.message?.includes("PENDING")) loadRun({ silent: true });
        return;
      }
      message.success(
        `Excluded ${res.data?.excluded ?? selectedMemberIds.length} member(s).`
      );
      setExcludeOpen(false);
      excludeForm.resetFields();
      setSelectedMemberIds([]);
      loadRun({ silent: true });
    } catch (err) {
      console.log(err);
      message.error("Unable to exclude members.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleInclude = () => {
    if (!runId || selectedMemberIds.length === 0) {
      message.error("Select at least one member to include.");
      return;
    }
    Modal.confirm({
      title: "Include in run",
      content: `Move ${selectedMemberIds.length} excluded member(s) back to Included?`,
      okText: "Include",
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = await includeLapseRunMembersApi(runId, {
            memberIds: selectedMemberIds,
          });
          if (!res || res.success === false) {
            message.error(
              res?.message ||
                "Unable to include members. The re-include API may not be available yet."
            );
            if (res?.message?.includes("PENDING")) loadRun({ silent: true });
            return Promise.reject();
          }
          message.success(
            `Included ${res.data?.included ?? selectedMemberIds.length} member(s).`
          );
          setSelectedMemberIds([]);
          await loadRun({ silent: true });
        } catch (err) {
          console.log(err);
          message.error(
            "Unable to include members. The re-include API may not be available yet."
          );
          return Promise.reject();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleApprove = () => {
    if (!runId) return;
    if (owningGroupId != null) {
      message.warning(
        "Clear the owning-group filter before approving. Approve currently applies to the entire run, not only the filtered group."
      );
      return;
    }
    Modal.confirm({
      title: "Approve lapse run",
      content: `You are about to lapse ${includedCount} included policies and send lapse SMS where configured. This cannot be undone from this screen. Continue?`,
      okText: "Approve",
      okButtonProps: { danger: true, style: { background: "#f04f05", borderColor: "#f04f05" } },
      onOk: async () => {
        const res = await approveLapseRunApi(runId);
        if (!res || res.success === false) {
          message.error(res?.message || "Unable to approve lapse run.");
          if (res?.message?.includes("PENDING")) loadRun({ silent: true });
          return Promise.reject();
        }
        message.success("Approval started…");
        await loadRun({ silent: true });
      },
    });
  };

  const handleReject = async () => {
    if (!runId) return;
    const values = await rejectForm.validateFields();
    setActionLoading(true);
    try {
      const res = await rejectLapseRunApi(runId, {
        ...(values.notes ? { notes: values.notes } : {}),
      });
      if (!res || res.success === false) {
        message.error(res?.message || "Unable to reject lapse run.");
        if (res?.message?.includes("PENDING")) loadRun({ silent: true });
        return;
      }
      message.success("Lapse run rejected.");
      setRejectOpen(false);
      rejectForm.resetFields();
      applyRun(res.data?.status ? res.data : { ...run!, status: "REJECTED" }, false);
      loadRun({ silent: true });
    } catch (err) {
      console.log(err);
      message.error("Unable to reject lapse run.");
    } finally {
      setActionLoading(false);
    }
  };

  const columns: ColumnsType<LapseRunMember> = [
    {
      title: "Name",
      dataIndex: "name",
      ellipsis: true,
      render: (_: any, row) => row.name || row.Member?.Name || "-",
    },
    {
      title: "Surname",
      dataIndex: "surname",
      ellipsis: true,
      render: (_: any, row) => row.surname || row.Member?.Surname || "-",
    },
    {
      title: "Policy number",
      dataIndex: "policyNumber",
      ellipsis: true,
      render: (value?: string | null) => value || "-",
    },
    {
      title: "Member group",
      ellipsis: true,
      render: (_: any, row) => row.MemberGroup?.GroupName || "-",
    },
    {
      title: "Owning group",
      ellipsis: true,
      render: (_: any, row) => row.OwningGroup?.GroupName || "-",
    },
    {
      title: "Outstanding premium",
      dataIndex: "outstandingPremium",
      render: formatCurrency,
    },
    {
      title: "Months behind",
      key: "monthsBehind",
      render: (_: any, row: LapseRunMember) => {
        const outstanding = Number(row.outstandingPremium);
        const premium = Number(
          row.totalPremium ?? row.Member?.TotalPremium
        );
        if (!(premium > 0) || !Number.isFinite(outstanding)) return "-";
        const behind = Math.abs(Math.min(outstanding, 0));
        if (behind <= 0) return "0";
        const months = Math.round((behind / premium) * 10) / 10;
        return `~${months}`;
      },
    },
    {
      title: "Total premium",
      dataIndex: "totalPremium",
      render: (value: any, row) =>
        formatCurrency(value ?? row.Member?.TotalPremium),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value: LapseRunMemberStatus) => (
        <Tag color={MEMBER_STATUS_COLOR[value] || "default"}>{value}</Tag>
      ),
    },
    {
      title: "Error",
      dataIndex: "errorMessage",
      ellipsis: true,
      render: (value?: string | null) =>
        value ? (
          <Typography.Text type="danger" ellipsis={{ tooltip: value }}>
            {value}
          </Typography.Text>
        ) : (
          "-"
        ),
    },
  ];

  const selectableStatus: LapseRunMemberStatus | null = isIncludedTab
    ? "INCLUDED"
    : isExcludedTab
      ? "EXCLUDED"
      : null;

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        {adminLayout ? <AdminSideBar /> : <SideBar />}
        <Layout className="site-layout">
          {adminLayout ? <AdminTopBar /> : <TopBar />}
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <Space style={{ marginBottom: 16 }}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(listPath)}
                >
                  Back
                </Button>
              </Space>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <h2 style={{ margin: 0 }}>
                  <ToolOutlined
                    style={{ paddingRight: "5px", color: "#f04f05" }}
                  />
                  Lapse run {run?.id ?? runId}
                  {run?.status && (
                    <Tag
                      color={RUN_STATUS_COLOR[run.status]}
                      style={{ marginLeft: 12 }}
                    >
                      {run.status}
                    </Tag>
                  )}
                  {(run?.OwningGroup?.GroupName || run?.owningGroupId != null) && (
                    <Tag style={{ marginLeft: 8 }}>
                      {run.OwningGroup?.GroupName ||
                        `Owning group ${run.owningGroupId}`}
                    </Tag>
                  )}
                </h2>
                <Space wrap>
                  {isExcludedTab ? (
                    <Button
                      icon={<PlusOutlined />}
                      disabled={!isPending || selectedMemberIds.length === 0}
                      loading={actionLoading}
                      onClick={handleInclude}
                    >
                      Include in run
                    </Button>
                  ) : (
                    <Button
                      icon={<StopOutlined />}
                      disabled={
                        !isPending ||
                        !isIncludedTab ||
                        selectedMemberIds.length === 0
                      }
                      onClick={() => setExcludeOpen(true)}
                    >
                      Exclude from run
                    </Button>
                  )}
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    disabled={!isPending}
                    onClick={() => setRejectOpen(true)}
                  >
                    Reject run
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    disabled={!isPending || owningGroupId != null}
                    onClick={handleApprove}
                    style={
                      isPending && owningGroupId == null
                        ? { background: "#f04f05", borderColor: "#f04f05" }
                        : undefined
                    }
                  >
                    Approve run
                  </Button>
                </Space>
              </div>

              {isProcessing && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Approval is processing in the background. This page will refresh automatically."
                />
              )}

              {run?.status === "FAILED" && run.errorMessage && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={run.errorMessage}
                />
              )}

              {isPending && owningGroupId != null && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Owning-group filter is active. Clear it before approving — approve currently applies to the entire run."
                />
              )}

              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8} md={4} lg={4}>
                  <Card>
                    <Statistic title="Proposed" value={run?.proposedCount ?? 0} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={5} lg={5}>
                  <Card>
                    <Statistic title="Included" value={includedCount} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={5} lg={5}>
                  <Card>
                    <Statistic title="Excluded" value={run?.excludedCount ?? 0} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={5} lg={5}>
                  <Card>
                    <Statistic title="Lapsed" value={run?.lapsedCount ?? 0} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={5} lg={5}>
                  <Card>
                    <Statistic title="Errors" value={run?.errorCount ?? 0} />
                  </Card>
                </Col>
              </Row>

              <Typography.Paragraph type="secondary">
                Created {formatDate(run?.createdAt)}
                {run?.reviewedBy != null && ` · Reviewed by ${run.reviewedBy}`}
                {run?.reviewedAt && ` (${formatDate(run.reviewedAt)})`}
                {run?.approvedBy != null && ` · Approved by ${run.approvedBy}`}
                {run?.approvedAt && ` (${formatDate(run.approvedAt)})`}
              </Typography.Paragraph>

              <Tabs
                type="card"
                style={{ marginBottom: 16 }}
                activeKey={memberStatus || "INCLUDED"}
                onChange={(key) =>
                  setMemberStatus(key as LapseRunMemberStatus)
                }
                items={MEMBER_STATUS_TABS.map((tab) => ({
                  key: tab.key,
                  label: tab.label,
                }))}
                tabBarExtraContent={
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Owning group"
                    style={{ minWidth: 220 }}
                    value={owningGroupId}
                    options={owningGroupOptions}
                    onChange={(value) => setOwningGroupId(value)}
                  />
                }
              />

              {isPending && isExcludedTab && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Select excluded members and choose Include in run to move them back to Included."
                />
              )}

              <Table
                rowKey={(row) => row.memberId}
                columns={columns}
                dataSource={run?.members || []}
                pagination={{ pageSize: 20 }}
                tableLayout="fixed"
                rowSelection={
                  canSelectMembers && selectableStatus
                    ? {
                        selectedRowKeys: selectedMemberIds,
                        onChange: (keys) =>
                          setSelectedMemberIds(
                            keys.map((key) => Number(key))
                          ),
                        getCheckboxProps: (row) => ({
                          disabled: row.status !== selectableStatus,
                        }),
                      }
                    : undefined
                }
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      <Modal
        title="Exclude from run"
        open={excludeOpen}
        onCancel={() => setExcludeOpen(false)}
        onOk={handleExclude}
        confirmLoading={actionLoading}
        okText="Exclude"
      >
        <Form form={excludeForm} layout="vertical">
          <Form.Item name="excludeReason" label="Reason (optional)">
            <Input.TextArea
              rows={3}
              placeholder="Payment promised this week"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject lapse run"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={handleReject}
        confirmLoading={actionLoading}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea
              rows={3}
              placeholder="Cancelled — premium batch still processing"
            />
          </Form.Item>
        </Form>
      </Modal>
    </LoadingOverlay>
  );
};

export default MaintenanceRunPage;
